import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { isMinor } from "@/lib/contracts";
import {
  Send,
  Copy,
  MessageCircle,
  Loader2,
  Plus,
  Trash2,
  UserCheck,
  ShieldCheck,
  ExternalLink,
  X,
  Clock,
  CheckCircle2,
  Ban,
} from "lucide-react";

const ROLES = [
  { value: "alumno", label: "Alumno/a" },
  { value: "tutor", label: "Tutor legal" },
];

function normalizePhoneForWa(phone) {
  let p = String(phone || "").replace(/\D/g, "");
  if (!p) return "";
  if (!p.startsWith("34")) p = "34" + p;
  return p;
}

function portalUrl(portal_url, token) {
  if (portal_url && portal_url.startsWith("http")) return portal_url;
  const origin = window.location.origin;
  return `${origin}/firmar/${token}`;
}

function StatusPill({ status }) {
  const map = {
    pending: { icon: Clock, cls: "bg-neutral-100 text-neutral-700", label: "Pendiente" },
    otp_sent: { icon: Send, cls: "bg-blue-100 text-blue-800", label: "OTP enviado" },
    otp_verified: { icon: ShieldCheck, cls: "bg-indigo-100 text-indigo-800", label: "OTP verificado" },
    signed: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800", label: "Firmado" },
    cancelled: { icon: Ban, cls: "bg-red-100 text-red-800", label: "Cancelado" },
    expired: { icon: Ban, cls: "bg-amber-100 text-amber-800", label: "Caducado" },
  };
  const c = map[status] || map.pending;
  const Ic = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-2 py-0.5 ${c.cls}`}>
      <Ic className="h-3 w-3" /> {c.label}
    </span>
  );
}

export default function SignatureRequestDialog({ open, contract, student, onClose, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [creating, setCreating] = useState(false);
  const [signers, setSigners] = useState([]);
  const [requireOtp, setRequireOtp] = useState(true);

  const minor = student ? isMinor(student.birth_date) : false;

  const buildInitialSigners = useCallback(() => {
    const base = [
      {
        role: "alumno",
        name: student?.name || "",
        dni: student?.dni || "",
        phone: student?.phone || "",
        email: student?.email || "",
      },
    ];
    if (minor) {
      base.push({ role: "tutor", name: "", dni: "", phone: "", email: "" });
    }
    return base;
  }, [minor, student?.dni, student?.email, student?.name, student?.phone]);

  const loadList = useCallback(async () => {
    if (!contract?.id) return;
    setLoadingList(true);
    try {
      const { data } = await api.get(`/contracts/${contract.id}/signature-requests`);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoadingList(false);
    }
  }, [contract?.id]);

  useEffect(() => {
    if (!open) return;
    setSigners(buildInitialSigners());
    setRequireOtp(true);
    loadList();
  }, [open, buildInitialSigners, loadList]);

  const updateSigner = (idx, key, value) => {
    setSigners((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };
  const removeSigner = (idx) => setSigners((prev) => prev.filter((_, i) => i !== idx));
  const addSigner = () =>
    setSigners((prev) => [...prev, { role: "tutor", name: "", dni: "", phone: "", email: "" }]);

  const create = async () => {
    for (const s of signers) {
      if (!s.name.trim()) {
        toast.error("Todos los firmantes deben tener nombre.");
        return;
      }
      if (requireOtp && !s.phone.trim()) {
        toast.error(`Falta el teléfono de ${s.name || "un firmante"} para enviar el OTP.`);
        return;
      }
    }
    setCreating(true);
    try {
      await api.post(`/contracts/${contract.id}/signature-requests`, {
        signers: signers.map((s) => ({
          role: s.role,
          name: s.name.trim(),
          dni: s.dni.trim(),
          phone: s.phone.trim(),
          email: s.email.trim(),
        })),
        require_otp: requireOtp,
      });
      toast.success("Solicitud(es) de firma creadas.");
      await loadList();
      if (typeof onChanged === "function") onChanged();
      setSigners(buildInitialSigners());
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const cancelRequest = async (req) => {
    if (!window.confirm(`¿Cancelar solicitud de firma de ${req.signer_name}?`)) return;
    try {
      await api.post(`/contracts/${contract.id}/signature-requests/${req.id}/cancel`);
      toast.success("Solicitud cancelada.");
      await loadList();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado al portapapeles.");
    } catch {
      toast.error("No se pudo copiar el enlace.");
    }
  };

  const waLink = (req) => {
    const p = normalizePhoneForWa(req.signer_phone);
    if (!p) return null;
    const url = portalUrl(req.portal_url, req.token);
    const msg =
      `Hola ${req.signer_name}. Aquí tienes el enlace para firmar tu contrato ` +
      `Nº ${contract?.numero} de ${student?.name ? "Natur Autoescuela" : "la autoescuela"}:\n\n${url}\n\n` +
      `Recibirás un código por SMS para validar tu identidad. Enlace válido hasta ${
        req.expires_at ? req.expires_at.slice(0, 10) : ""
      }.`;
    return `https://wa.me/${p}?text=${encodeURIComponent(msg)}`;
  };

  const anySigned = useMemo(
    () => requests.some((r) => r.status === "signed"),
    [requests],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="signature-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-orange-500" />
            Firma remota — Contrato Nº {contract?.numero}
          </DialogTitle>
          <DialogDescription>
            Genera enlaces de firma remota para el alumno y, si es menor, para su tutor legal. Cada enlace incluye código OTP y auditoría.
          </DialogDescription>
        </DialogHeader>

        {/* --- Existing requests --- */}
        <div className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500">Solicitudes activas</h3>
          {loadingList ? (
            <div className="text-sm text-neutral-500">Cargando…</div>
          ) : requests.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-xs text-neutral-500 text-center">
              No hay solicitudes de firma para este contrato.
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map((r) => {
                const url = portalUrl(r.portal_url, r.token);
                const wa = waLink(r);
                return (
                  <div
                    key={r.id}
                    className="rounded-xl border border-neutral-200 bg-white p-3 space-y-2"
                    data-testid={`sig-req-${r.id}`}
                  >
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="flex-1 min-w-[140px]">
                        <div className="font-semibold text-sm">{r.signer_name}</div>
                        <div className="text-[11px] text-neutral-500">
                          {r.signer_role === "tutor" ? "Tutor legal" : r.signer_role} · {r.signer_phone || "sin teléfono"}
                        </div>
                      </div>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 max-w-[220px] truncate"
                        data-testid={`sig-open-${r.id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {url.replace(/^https?:\/\//, "").slice(0, 40)}…
                      </a>
                      <button
                        type="button"
                        onClick={() => copyLink(url)}
                        data-testid={`sig-copy-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 px-2.5 py-1 text-[11px] font-semibold text-neutral-700"
                      >
                        <Copy className="h-3 w-3" /> Copiar enlace
                      </button>
                      {wa && (
                        <a
                          href={wa}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`sig-wa-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 px-2.5 py-1 text-[11px] font-semibold text-green-800"
                        >
                          <MessageCircle className="h-3 w-3" /> WhatsApp
                        </a>
                      )}
                      {r.status !== "signed" && r.status !== "cancelled" && (
                        <button
                          type="button"
                          onClick={() => cancelRequest(r)}
                          data-testid={`sig-cancel-${r.id}`}
                          className="ml-auto inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white hover:bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700"
                        >
                          <X className="h-3 w-3" /> Cancelar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* --- Create new --- */}
        {!contract?.frozen && (
          <div className="space-y-3 pt-4 border-t border-neutral-100">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                Solicitar nueva firma
              </h3>
              {anySigned && (
                <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  Ya hay firmas; nuevas solicitudes se registrarán aparte
                </span>
              )}
            </div>

            <div className="space-y-2">
              {signers.map((s, idx) => (
                <div key={idx} className="rounded-xl border border-neutral-200 p-3 space-y-2" data-testid={`signer-block-${idx}`}>
                  <div className="flex items-center gap-2">
                    <select
                      value={s.role}
                      onChange={(e) => updateSigner(idx, "role", e.target.value)}
                      className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs font-semibold"
                      data-testid={`signer-role-${idx}`}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    {signers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSigner(idx)}
                        className="ml-auto text-red-600 hover:text-red-700"
                        data-testid={`signer-remove-${idx}`}
                        title="Quitar firmante"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Nombre completo *"
                      value={s.name}
                      onChange={(e) => updateSigner(idx, "name", e.target.value)}
                      className="h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid={`signer-name-${idx}`}
                    />
                    <input
                      type="text"
                      placeholder="DNI / NIE"
                      value={s.dni}
                      onChange={(e) => updateSigner(idx, "dni", e.target.value)}
                      className="h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid={`signer-dni-${idx}`}
                    />
                    <input
                      type="tel"
                      placeholder="Teléfono (para OTP)"
                      value={s.phone}
                      onChange={(e) => updateSigner(idx, "phone", e.target.value)}
                      className="h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid={`signer-phone-${idx}`}
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={s.email}
                      onChange={(e) => updateSigner(idx, "email", e.target.value)}
                      className="h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid={`signer-email-${idx}`}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addSigner}
                data-testid="signer-add"
                className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-semibold"
              >
                <Plus className="h-3.5 w-3.5" /> Añadir firmante
              </button>
            </div>

            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={requireOtp}
                onChange={(e) => setRequireOtp(e.target.checked)}
                data-testid="sig-require-otp"
                className="rounded border-neutral-300"
              />
              <span>Requerir código OTP para firmar (recomendado)</span>
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary">Cerrar</button>
              <button
                type="button"
                onClick={create}
                disabled={creating}
                className="btn-primary"
                data-testid="sig-create-submit"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Crear solicitud
              </button>
            </div>
          </div>
        )}

        {contract?.frozen && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs" data-testid="contract-frozen-notice">
            <b>Contrato congelado</b> — todas las firmas han sido completadas. No pueden crearse nuevas solicitudes.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
